import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Scales, Flag, PaintBrush, Trash, CheckCircle, Trophy, Fire } from "@phosphor-icons/react";

const SUBJECTS = [
  ["constitutional", "Constitutional"], ["contract", "Contract"], ["criminal", "Criminal"],
  ["tort", "Tort"], ["property", "Property"], ["corporate", "Corporate"],
  ["ip", "IP"], ["international", "International"],
];
const DIFFICULTIES = ["pupil", "associate", "partner", "senior_advocate"];

export default function LegalGameAdmin() {
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(false);

  const [seed, setSeed] = useState({ subject: "contract", country: "Ghana", system: "common", difficulty: "associate", count: 10 });
  const [seeding, setSeeding] = useState(false);
  const [slide, setSlide] = useState({ index: 0, prompt: "" });
  const [genImg, setGenImg] = useState(false);
  const [slidePreviewUrl, setSlidePreviewUrl] = useState(null);
  const [seasonName, setSeasonName] = useState("");
  const [seasonDays, setSeasonDays] = useState(3);
  const [seasonBusy, setSeasonBusy] = useState(false);
  const [warming, setWarming] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r, se] = await Promise.all([
        api.get("/admin/legal-game/stats"),
        api.get("/admin/legal-game/reports"),
        api.get("/admin/legal-game/seasons").catch(() => ({ data: [] })),
      ]);
      setStats(s.data); setReports(r.data); setSeasons(se.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runSeed = async () => {
    setSeeding(true);
    try {
      const r = await api.post("/admin/legal-game/seed", {
        subject: seed.subject,
        jurisdiction_country: seed.country,
        jurisdiction_system: seed.system,
        difficulty: seed.difficulty,
        count: Number(seed.count) || 10,
      });
      toast.success(`Seeded ${r.data.inserted} questions`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Seed failed");
    } finally { setSeeding(false); }
  };

  const act = async (qid, action) => {
    try {
      await api.post(`/admin/legal-game/questions/${qid}/action`, { action });
      toast.success(`Question ${action}d`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const genSlideImg = async () => {
    if (!slide.prompt.trim()) { toast.error("Enter a prompt"); return; }
    setGenImg(true);
    setSlidePreviewUrl(null);
    try {
      const r = await api.post("/admin/legal-game/landing/slide-image", {
        slide_index: Number(slide.index) || 0,
        prompt: slide.prompt,
      });
      toast.success(`Slide ${slide.index} image generated — preview below.`);
      setSlidePreviewUrl(r.data.url);
      setSlide(s => ({ ...s, prompt: "" }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Image generation failed");
    } finally { setGenImg(false); }
  };

  const createSeason = async () => {
    if (!seasonName.trim()) { toast.error("Enter a season name"); return; }
    if (!window.confirm(`Create season "${seasonName}"? Only one active season is allowed.`)) return;
    setSeasonBusy(true);
    try {
      await api.post("/admin/legal-game/season", {
        name: seasonName.trim(),
        round_days: Number(seasonDays) || 3,
      });
      toast.success("Season created");
      setSeasonName("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create season");
    } finally { setSeasonBusy(false); }
  };

  const advanceRound = async (sid) => {
    if (!window.confirm("Apply elimination cutoff for the just-closed round?")) return;
    try {
      const r = await api.post(`/admin/legal-game/season/${sid}/advance-round`);
      toast.success(`Round ${r.data.round_processed} processed · ${r.data.eliminated} eliminated`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const concludeSeason = async (sid) => {
    if (!window.confirm("Crown the champion and close the season?")) return;
    try {
      const r = await api.post(`/admin/legal-game/season/${sid}/conclude`);
      toast.success(r.data.champion ? `Champion crowned: ${r.data.champion}` : "Season concluded (no champion)");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const warmCacheNow = async () => {
    if (!window.confirm("Run the AI cache warmer now? This may take several minutes and burn Gemini credits.")) return;
    setWarming(true);
    try {
      await api.post("/admin/legal-game/cache/warm-now");
      toast.success("Cache warmer started in background — check /cache/status");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setWarming(false); }
  };

  return (
    <div className="space-y-4" data-testid="legal-admin-panel">
      <Card className="bg-[#111] border-white/10">
        <CardHeader><CardTitle className="flex items-center gap-2"><Scales size={20} weight="fill" className="text-emerald-400"/> Legal Game Overview</CardTitle></CardHeader>
        <CardContent>
          {loading && <div className="text-white/50 text-sm">Loading…</div>}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
              <Stat label="Total questions" value={stats.questions.total} />
              <Stat label="Active" value={stats.questions.active} color="text-emerald-400" />
              <Stat label="Flagged" value={stats.questions.flagged} color="text-amber-400" />
              <Stat label="Removed" value={stats.questions.removed} color="text-rose-400" />
              <Stat label="Players" value={stats.players} />
              <Stat label="Finished sessions" value={stats.finished_sessions} />
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-[#111] border-white/10">
        <CardHeader><CardTitle className="flex items-center gap-2"><Flag size={18}/> Reported / flagged questions</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {reports.length === 0 && <div className="text-white/50 text-sm">No reports yet.</div>}
          {reports.map(q => (
            <Card key={q.question_id} className="bg-black/30 border-white/10">
              <CardContent className="p-3 space-y-2">
                <div className="flex flex-wrap gap-2 text-xs">
                  <Badge className="bg-rose-500/20 border-rose-500/40 text-rose-300">Reports: {q.report_count}</Badge>
                  <Badge className="bg-white/10 border-white/20">{q.subject}</Badge>
                  <Badge className="bg-white/10 border-white/20">{q.jurisdiction_country} · {q.jurisdiction_system}</Badge>
                  <Badge className="bg-white/10 border-white/20">{q.difficulty}</Badge>
                  <Badge className="bg-white/10 border-white/20">Status: {q.status}</Badge>
                </div>
                <div className="text-sm text-white/90">{q.question_text}</div>
                <ul className="text-xs text-white/60 list-disc list-inside">
                  {(q.options || []).map((o, i) => (
                    <li key={i} className={i === q.correct_option ? "text-emerald-300" : ""}>{o}</li>
                  ))}
                </ul>
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" className="border-emerald-500/40 text-emerald-300 gap-1" onClick={() => act(q.question_id, "approve")}>
                    <CheckCircle size={14} weight="fill"/> Approve
                  </Button>
                  <Button size="sm" variant="outline" className="border-amber-500/40 text-amber-300 gap-1" onClick={() => act(q.question_id, "flag")}>
                    <Flag size={14} weight="fill"/> Keep flagged
                  </Button>
                  <Button size="sm" variant="outline" className="border-rose-500/40 text-rose-300 gap-1" onClick={() => act(q.question_id, "remove")}>
                    <Trash size={14} weight="fill"/> Remove
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </CardContent>
      </Card>

      <Card className="bg-[#111] border-white/10">
        <CardHeader><CardTitle>Seed questions (Gemini)</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-5 gap-2 items-end">
          <div>
            <label className="text-xs text-white/50 mb-1 block">Subject</label>
            <Select value={seed.subject} onValueChange={v=>setSeed(s=>({...s,subject:v}))}>
              <SelectTrigger className="bg-black/40 border-white/10"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                {SUBJECTS.map(([k,l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-white/50 mb-1 block">Country</label>
            <Input value={seed.country} onChange={e=>setSeed(s=>({...s,country:e.target.value}))} className="bg-black/40 border-white/10" />
          </div>
          <div>
            <label className="text-xs text-white/50 mb-1 block">System</label>
            <Select value={seed.system} onValueChange={v=>setSeed(s=>({...s,system:v}))}>
              <SelectTrigger className="bg-black/40 border-white/10"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                <SelectItem value="common">Common</SelectItem>
                <SelectItem value="civil">Civil</SelectItem>
                <SelectItem value="mixed">Mixed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-white/50 mb-1 block">Difficulty</label>
            <Select value={seed.difficulty} onValueChange={v=>setSeed(s=>({...s,difficulty:v}))}>
              <SelectTrigger className="bg-black/40 border-white/10"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                {DIFFICULTIES.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-white/50 mb-1 block">Count</label>
            <Input type="number" min={1} max={15} value={seed.count} onChange={e=>setSeed(s=>({...s,count:e.target.value}))} className="bg-black/40 border-white/10"/>
          </div>
          <Button data-testid="legal-seed-btn" disabled
                    className="md:col-span-5 bg-white/10 text-white/70 cursor-not-allowed">
            Feature temporarily paused for maintenance
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-[#111] border-white/10">
        <CardHeader><CardTitle className="flex items-center gap-2"><Trophy size={18} weight="fill" className="text-amber-300"/> Moot Season lifecycle</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid md:grid-cols-4 gap-2 items-end">
            <div className="md:col-span-2">
              <label className="text-xs text-white/50 mb-1 block">Season name</label>
              <Input value={seasonName} onChange={e=>setSeasonName(e.target.value)} placeholder="e.g. Season 1 — Grand Opening" className="bg-black/40 border-white/10"/>
            </div>
            <div>
              <label className="text-xs text-white/50 mb-1 block">Days per round</label>
              <Input type="number" min={1} max={30} value={seasonDays} onChange={e=>setSeasonDays(e.target.value)} className="bg-black/40 border-white/10"/>
            </div>
            <Button data-testid="legal-season-create-btn" onClick={createSeason} disabled={seasonBusy} className="bg-amber-500 hover:bg-amber-600 text-black font-bold">
              {seasonBusy ? "Creating…" : "Create season"}
            </Button>
          </div>
          <div className="space-y-2">
            {seasons.length === 0 && <div className="text-white/50 text-sm">No seasons yet.</div>}
            {seasons.map(s => (
              <Card key={s.season_id} className="bg-black/30 border-white/10">
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div>
                      <div className="font-heading font-bold">{s.name}</div>
                      <div className="text-xs text-white/50">
                        {s.starts_at?.slice(0,10)} → {s.ends_at?.slice(0,10)} · <Badge className="bg-white/10 border-white/20 text-[10px] px-1.5 py-0">{s.status}</Badge>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {s.status !== "concluded" && (
                        <>
                          <Button size="sm" variant="outline" className="border-amber-500/40 text-amber-300" onClick={() => advanceRound(s.season_id)}>Advance round</Button>
                          <Button size="sm" variant="outline" className="border-emerald-500/40 text-emerald-300" onClick={() => concludeSeason(s.season_id)}>Conclude</Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-[#111] border-white/10">
        <CardHeader><CardTitle className="flex items-center gap-2"><Fire size={18} weight="fill" className="text-orange-400"/> AI Cache warmer</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-white/60">
            A background job runs daily to keep every (subject × jurisdiction × difficulty) bucket topped up
            to 40+ questions. You can trigger it manually below.
          </p>
          <Button data-testid="legal-cache-warm-btn" onClick={warmCacheNow} disabled={warming} className="bg-orange-500 hover:bg-orange-600 text-black font-bold">
            {warming ? "Starting…" : "Run cache warmer now"}
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-[#111] border-white/10">
        <CardHeader><CardTitle className="flex items-center gap-2"><PaintBrush size={18} weight="fill" className="text-fuchsia-400"/> Landing slideshow — Nano Banana image generation</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid md:grid-cols-6 gap-2 items-end">
            <div>
              <label className="text-xs text-white/50 mb-1 block">Slide index (0-4)</label>
              <Input type="number" min={0} max={20} value={slide.index} onChange={e=>setSlide(s=>({...s,index:e.target.value}))} className="bg-black/40 border-white/10"/>
            </div>
            <div className="md:col-span-4">
              <label className="text-xs text-white/50 mb-1 block">Prompt</label>
              <Input placeholder="e.g. cinematic collage of law students in a modern library with digital scales overlay, futuristic tone" value={slide.prompt} onChange={e=>setSlide(s=>({...s,prompt:e.target.value}))} className="bg-black/40 border-white/10"/>
            </div>
            <Button data-testid="slide-image-gen-btn" disabled
                     className="bg-white/10 text-white/70 cursor-not-allowed">
              Feature temporarily paused for maintenance
            </Button>
          </div>
          {slidePreviewUrl && (
            <div className="space-y-2" data-testid="slide-image-preview">
              <div className="text-[11px] text-white/50 uppercase tracking-widest font-bold">Latest generated image</div>
              <img
                src={slidePreviewUrl}
                alt="Latest slide"
                className="max-w-md w-full rounded-lg border border-fuchsia-500/40"
              />
              <div className="text-[11px] text-white/50 break-all">
                Saved to slide index <span className="text-white/80">{slide.index}</span> · <a href={slidePreviewUrl} target="_blank" rel="noreferrer" className="text-fuchsia-300 underline">Open in new tab</a>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="bg-black/30 border border-white/10 rounded-lg p-3">
      <div className="text-xs text-white/50 uppercase">{label}</div>
      <div className={`text-2xl font-black ${color || ""}`}>{value ?? 0}</div>
    </div>
  );
}
